import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from 'react'
import useFeatureToggle from '@/hooks/useFeatureToggle'
import { FeatureToggle } from '@/resources/enums/featureToggle'
import {
  getIsAutomaticPrintEnabledValue,
  setAutomaticPrintEnabledValue,
} from '@/resources/utils'
import { AutomaticPrintContextProps } from './types'

const AutomaticPrintContext = createContext<AutomaticPrintContextProps | null>(
  null
)

export const AutomaticPrintProvider = ({ children }: PropsWithChildren) => {
  const isAutomaticPrintAvailable =
    useFeatureToggle({
      splitName: 'automatic_order_print',
    }) === FeatureToggle.ON

  const [isAutomaticPrintEnabled, setIsAutomaticPrintEnabled] = useState(false)

  useEffect(() => {
    setIsAutomaticPrintEnabled(
      getIsAutomaticPrintEnabledValue() && isAutomaticPrintAvailable
    )
  }, [isAutomaticPrintAvailable])

  const updateAutomaticPrintStatus = (isEnabled: boolean) => {
    setAutomaticPrintEnabledValue(isEnabled)
    setIsAutomaticPrintEnabled(isEnabled)
  }

  return (
    <AutomaticPrintContext.Provider
      value={{
        isAutomaticPrintAvailable,
        isAutomaticPrintEnabled,
        updateAutomaticPrintStatus,
      }}
    >
      {children}
    </AutomaticPrintContext.Provider>
  )
}

export const useAutomaticPrint = () => {
  const context = useContext(AutomaticPrintContext)
  if (context === null) {
    throw new Error(
      'useAutomaticPrint must be used within an AutomaticPrintProvider'
    )
  }
  return context
}
